import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { Application } from '../../models/application/application.interface';
import { Supervisor } from '../../models/participant/supervisor.interface';
import { ApplicationStatus } from '../../models/application/application-status.interface';
import { ApplicationStatusChangeRequest } from '../../models/application/application-status-change-request.interface';
import { ApplicationSaveCommentRequest } from '../../models/application/application-save-comment-request.interface';
import { ApplicationCommentResponse } from '../../models/application/application-comment-response.interface';
import { EligibilityEventHistory } from '../../models/application/eligibility-event-history.interface';
import { RuleException } from '../../models/application/rule-exception.interface';
import { MessageSource } from '../../models/application/message-source.interface';
import { ApplicationTotals } from '../../models/application/application-totals.interface';
import { Course } from '../../models/application/course.interface';
import { ApplicationSessionInfo } from '../../models/application/session-info.interface';
import { ApplicationSearchResult } from '../../models/application/application-search-result.interface';
import { ApplicationExpense } from '../../models/application/application-expense.interface';
import { HttpParamObject } from '../../utility/http-utility/http-param-object.interface';
import { HttpUtilityService } from '../../utility/http-utility/http-utility.service';
import { BenefitPeriod } from '../../models/application/benefit-period.interface';
import { EducationGoal } from '../../models/education-goal/education-goal.interface';
import { Expense } from '../../models/application/expense.interface';
import { ExpenseType } from '../../models/application/expense-type.interface';
import { ExpensesRefresh } from '../../models/expenses/expenses-refresh.interface';
import { HttpPatchUtilityService } from '../../utility/http-patch-utility/http-patch-utility.service';
import { catchError, first, flatMap, tap } from 'rxjs/operators';
import { Operation } from 'fast-json-patch';
import { Savings, SavingsSubCategory } from '../../models/savings/savings.interface';

import * as clone from 'clone';
import {
	constants,
	CreatePartialRequest,
	CreditHoursService,
	LodashUtilityService,
	PRODUCT_TYPES
} from '@edassist/common';
import {
	ApplicationCommentsPatch,
	ApplicationDetail,
	ApplicationDetailPatch,
	ApplicationLearningType,
	ApplicationPendingReviewQueue,
	ApplicationSearchQuery,
	ApplicationSubmission,
	ApplicationType,
	BookEligibility,
	CombinedExpense,
	FlatApplication,
	LoanPaymentDetail,
	MicroApplication,
	ApplicationHistoryService
} from '@edassist/api';
import { ApplicationPaymentVerification } from '../../models/application/application-payment-verification.interface';
import { CustomHttpParams } from '../../models/http/custom-http-params.class';
import { CacheBucket, withCache } from '@ngneat/cashew';

@Injectable({ providedIn: 'root' })
export class ApplicationService {
	applicationData: Observable<Application | EducationGoal>;
	applicationStatusHistoryData: Observable<Array<EligibilityEventHistory>>;
	approvalHistoryData: Observable<Array<Supervisor>>;
	$applicationCoursesData: Observable<Array<Course>>;
	$applicationTotalsData: Observable<ApplicationTotals>;
	$applicationExpensesData: Observable<Array<ApplicationExpense>>;
	$applicationCourseExpensesData: Observable<ExpensesRefresh>;
	$applicationTotalCreditHours: Observable<number>;
	private readonly applicationDataSubject: BehaviorSubject<Application | EducationGoal>;
	private readonly applicationStatusHistorySubject: BehaviorSubject<Array<EligibilityEventHistory>>;
	private readonly approvalHistorySubject: BehaviorSubject<Array<Supervisor>>;
	private readonly applicationCoursesSubject: BehaviorSubject<Array<Course>>;
	private readonly applicationTotalsSubject: BehaviorSubject<ApplicationTotals>;
	private readonly applicationExpensesSubject: BehaviorSubject<Array<ApplicationExpense>>;
	private readonly applicationCourseExpensesSubject: BehaviorSubject<ExpensesRefresh>;
	private readonly applicationTotalCreditHoursSubject: BehaviorSubject<number>;
	private readonly addExceptionsBucket = new CacheBucket();
	private readonly relativeUrl = 'applications/';

	static generatePatchObject(application: Application, updates: object): Array<Operation> {
		return HttpPatchUtilityService.convertDataToHttpPatchOperation(
			application,
			LodashUtilityService.merge(clone(application), updates)
		);
	}

	constructor(
		private readonly http: HttpClient,
		private readonly applicationHistoryService: ApplicationHistoryService
	) {
		this.applicationDataSubject = new BehaviorSubject<Application | EducationGoal>(undefined);
		this.applicationData = this.applicationDataSubject.asObservable();
		this.applicationStatusHistorySubject = new BehaviorSubject<Array<EligibilityEventHistory>>(undefined);
		this.applicationStatusHistoryData = this.applicationStatusHistorySubject.asObservable();
		this.approvalHistorySubject = new BehaviorSubject<Array<Supervisor>>(undefined);
		this.approvalHistoryData = this.approvalHistorySubject.asObservable();
		this.applicationCoursesSubject = new BehaviorSubject<Array<Course>>(undefined);
		this.$applicationCoursesData = this.applicationCoursesSubject.asObservable();
		this.applicationTotalsSubject = new BehaviorSubject<ApplicationTotals>(undefined);
		this.$applicationTotalsData = this.applicationTotalsSubject.asObservable();
		this.applicationExpensesSubject = new BehaviorSubject<Array<ApplicationExpense>>(undefined);
		this.$applicationExpensesData = this.applicationExpensesSubject.asObservable();
		this.applicationCourseExpensesSubject = new BehaviorSubject<ExpensesRefresh>(undefined);
		this.$applicationCourseExpensesData = this.applicationCourseExpensesSubject.asObservable();
		this.applicationTotalCreditHoursSubject = new BehaviorSubject<number>(undefined);
		this.$applicationTotalCreditHours = this.applicationTotalCreditHoursSubject.asObservable();
	}

	saveApplicationComment(
		applicationId: number,
		applicationCommentsDTO: ApplicationSaveCommentRequest
	): Observable<ApplicationCommentResponse> {
		return this.http.post<ApplicationCommentResponse>(
			`${this.relativeUrl}${applicationId.toString()}/comments`,
			applicationCommentsDTO
		);
	}

	patchApplicationComment(
		applicationId: number,
		comment: ApplicationSaveCommentRequest,
		updates: ApplicationCommentsPatch
	): Observable<ApplicationSaveCommentRequest> {
		const patchOperation = HttpPatchUtilityService.convertDataToHttpPatchOperation(comment, {
			...comment,
			...updates
		});

		return this.http.patch<ApplicationSaveCommentRequest>(
			`${this.relativeUrl}${applicationId.toString()}/comments/${comment.id}`,
			patchOperation
		);
	}

	reviewApplicationComment(applicationId: number, messageSourceDTO: MessageSource): Observable<void> {
		return this.http.post<void>(
			`${this.relativeUrl}${applicationId.toString()}/comments/${messageSourceDTO.entityId.toString()}/review`,
			messageSourceDTO
		);
	}

	changeApplicationStatus(
		applicationId: number,
		applicationStatusChangeDTO: ApplicationStatusChangeRequest,
		isUpdateBenefitPeriod?: boolean
	): Observable<ApplicationStatus> {
		let queryParams: HttpParamObject;
		if (isUpdateBenefitPeriod) {
			queryParams = HttpUtilityService.toPlainHttpParams({
				isUpdateBenefitPeriod
			});
		}

		return this.http.post<ApplicationStatus>(
			`${this.relativeUrl}${applicationId.toString()}/application-status`,
			applicationStatusChangeDTO,
			queryParams
		);
	}

	getApplication(applicationId: number): void {
		this.http
			.get<Application>(`${this.relativeUrl}${applicationId.toString()}`)
			.pipe(catchError(() => of(undefined)))
			.subscribe((application: Application) => {
				// Refresh all components who need application data:
				this.applicationDataSubject.next(application);
			});
	}

	getApplicationData(applicationId: number): Observable<Application> {
		return this.http.get<Application>(`${this.relativeUrl}${applicationId.toString()}`).pipe(
			catchError(() => of(undefined)),
			tap((application: Application) => {
				// Update the BehaviorSubject for other components use case and this side effect won't impact existing consumers
				this.applicationDataSubject.next(application);
			})
		);
	}

	getApplicationCourses(applicationId: number, applicationLevelCurrency: boolean): void {
		this.http
			.get<
				Array<Course>
			>(`${this.relativeUrl}${applicationId.toString()}/application-courses`, { params: this.getApplicationLevelQueryParam(applicationLevelCurrency) })
			.subscribe((courses: Array<Course>) => {
				this.applicationCoursesSubject.next(courses);
			});
	}

	patchApplication(
		applicationId: number,
		patchRequest: Array<Operation>,
		isUpdateBenefitPeriod: boolean,
		actionCode?: string
	): Observable<Application> {
		let params = new HttpParams();

		if (typeof isUpdateBenefitPeriod === 'boolean') {
			params = params.append('isUpdateBenefitPeriod', isUpdateBenefitPeriod);
		}
		if (actionCode) {
			params = params.append('actionCode', actionCode);
		}

		return this.http.patch<Application>(`${this.relativeUrl}${applicationId}`, patchRequest, { params });
	}

	patchApplicationCourse(
		applicationId: number,
		applicationCourseId: number,
		patchRequest: Array<Operation>,
		queue = false,
		applicationLevelCurrency: boolean
	): Observable<Course> {
		const customParams = new CustomHttpParams(queue);

		return this.http.patch<Course>(
			`${this.relativeUrl}${applicationId}/application-courses/${applicationCourseId}?applicationLevelCurrency=${applicationLevelCurrency}`,
			patchRequest,
			{ params: customParams }
		);
	}

	patchApplicationDetail(
		applicationId: number,
		appDetail: ApplicationDetail | LoanPaymentDetail,
		updates: ApplicationDetailPatch,
		params?: object
	): Observable<ApplicationDetail> {
		const queryParams: HttpParamObject = HttpUtilityService.toPlainHttpParams(params);
		const patchOperation = HttpPatchUtilityService.convertDataToHttpPatchOperation(appDetail, {
			...appDetail,
			...updates
		});

		return this.http.patch<ApplicationDetail>(
			`${this.relativeUrl}${applicationId}/detail/${appDetail.id}`,
			patchOperation,
			queryParams
		);
	}

	getApplicationDetail(applicationId: number): Observable<ApplicationDetail> {
		return this.http.get<ApplicationDetail>(`${this.relativeUrl}${applicationId}/detail`);
	}

	patchCourseExpense(
		applicationId: number,
		courseId: number,
		expenseId: number,
		patchRequest: Array<Operation>,
		applicationLevelCurrency: boolean
	): Observable<Expense> {
		return this.http.patch<Expense>(
			`${this.relativeUrl}${applicationId}/courses/${courseId}/expenses/${expenseId}`,
			patchRequest,
			{ params: this.getApplicationLevelQueryParam(applicationLevelCurrency) }
		);
	}

	patchApplicationExpense(
		applicationId: number,
		expenseId: number,
		patchRequest: Array<Operation>,
		applicationLevelCurrency: boolean
	): Observable<ApplicationExpense> {
		return this.http.patch<ApplicationExpense>(
			`${this.relativeUrl}${applicationId}/application-expenses/${expenseId}`,
			patchRequest,
			{ params: this.getApplicationLevelQueryParam(applicationLevelCurrency) }
		);
	}

	getApplicationStatusHistory(applicationId: number, forceUpdate: boolean = true): void {
		this.applicationHistoryService
			.getApplicationStatusHistory(
				applicationId,
				this.applicationDataSubject.value?.participantId,
				this.applicationDataSubject.value?.benefitPeriod?.program?.clientId,
				forceUpdate
			)
			.subscribe({
				next: (items) => {
					this.applicationStatusHistorySubject.next(items);
				},
				error: () => {
					// subscribing to BehaviorSubject.error only gets notified the first time?
					this.applicationStatusHistorySubject.next(null);
				}
			});
	}

	deleteApplicationException(applicationId: number, exceptionId: number): Observable<void> {
		return this.http.delete<void>(
			`${this.relativeUrl}${applicationId.toString()}/rule-exception/${exceptionId.toString()}`
		);
	}

	getApplicationStatusHistoryFromObservable(
		applicationId: number,
		forceUpdate: boolean = true
	): Observable<Array<EligibilityEventHistory>> {
		return this.applicationHistoryService.getApplicationStatusHistory(
			applicationId,
			this.applicationDataSubject.value?.participantId,
			this.applicationDataSubject.value?.benefitPeriod?.program?.clientId,
			forceUpdate
		);
	}

	getParticipantApprovers(applicationId: number): void {
		this.http
			.get<Array<Supervisor>>(`${this.relativeUrl}${applicationId}/supervisors`)
			.subscribe((supervisorData: Array<Supervisor>) => {
				this.approvalHistorySubject.next(supervisorData);
			});
	}

	bypassApplicationReviewers(applicationId: number, supervisor: Supervisor, bypassFlag: boolean): Observable<boolean> {
		return this.http.post<boolean>(
			`${this.relativeUrl}${applicationId}/supervisors/bypass?bypassFlag=${bypassFlag}`,
			supervisor
		);
	}

	saveApplicationApprovers(applicationId: number, approver: Supervisor): Observable<Supervisor> {
		return this.http.post<Supervisor>(`${this.relativeUrl}${applicationId}/supervisors`, approver);
	}

	resolveRule(applicationId: number, ruleId: number, ruleException: RuleException): Observable<void> {
		return this.http.post<void>(
			`${this.relativeUrl}${applicationId.toString()}/rule-messages/${ruleId.toString()}/add-rule-exception`,
			ruleException
		);
	}

	getExceptionRules(client: number): Observable<Array<RuleException>> {
		return this.http.get<Array<RuleException>>(`exception-rules/client/${client.toString()}`, {
			context: withCache({
				bucket: this.addExceptionsBucket
			})
		});
	}

	getApplicationTotals(applicationId: number, applicationLevelCurrency: boolean): void {
		this.http
			.get<ApplicationTotals>(`${this.relativeUrl}${applicationId.toString()}/totals`, {
				params: this.getApplicationLevelQueryParam(applicationLevelCurrency)
			})
			.subscribe((totals: ApplicationTotals) => {
				this.applicationTotalsSubject.next(totals);
			});
	}

	saveApplicationSessionInfo(
		applicationId: number,
		applicationSessionInfo: ApplicationSessionInfo
	): Observable<ApplicationSessionInfo> {
		return this.http.post<ApplicationSessionInfo>(
			`${this.relativeUrl}${applicationId}/session-info`,
			applicationSessionInfo
		);
	}

	checkBenefitPeriodChange(
		applicationId: number,
		applicationSessionInfo: ApplicationSessionInfo,
		actionCode?: string
	): Observable<boolean> {
		const queryParams = HttpUtilityService.toPlainHttpParams({ actionCode });

		return this.http.post<boolean>(
			`${this.relativeUrl}${applicationId}/check-benefit-period-change`,
			applicationSessionInfo,
			{
				...(actionCode ? queryParams : {})
			}
		);
	}

	getApplicationBenefitPeriod(applicationId: number): Observable<BenefitPeriod> {
		return this.http.get<Array<BenefitPeriod>>(`${this.relativeUrl}${applicationId}/benefit-periods`).pipe(
			flatMap((benefitPeriod) => benefitPeriod),
			first()
		);
	}

	searchApplications(applicationSearchQuery: ApplicationSearchQuery): Observable<ApplicationSearchResult> {
		const queryParams: HttpParamObject = HttpUtilityService.toPlainHttpParams(applicationSearchQuery);

		return this.http.get<ApplicationSearchResult>(`${this.relativeUrl}search`, queryParams);
	}

	refreshApplictionStatusHistoryData(eligibilityEventHistory: Array<EligibilityEventHistory>): void {
		this.applicationStatusHistorySubject.next(eligibilityEventHistory);
	}

	clearApplicationData(): void {
		// Reset all RXJS behavior subjects driving application data
		// in the case of a reload of the application route from that same route
		// For example, an app number search while on the application route
		this.applicationDataSubject.next(undefined);
		this.applicationStatusHistorySubject.next(undefined);
		this.applicationCoursesSubject.next(undefined);
		this.approvalHistorySubject.next(undefined);
		this.applicationTotalsSubject.next(undefined);
		this.applicationExpensesSubject.next(undefined);
		this.applicationCourseExpensesSubject.next(undefined);
	}

	isReadyForAdminApproval(applicationId: number): Observable<void> {
		return this.http.get<void>(`${this.relativeUrl}${applicationId}/is-admin-approval-ready`);
	}

	getApplicationLearningTypes(learningTypeId: number): Observable<ApplicationLearningType> {
		return learningTypeId ?
				this.http.get<ApplicationLearningType>(`learning-types/${learningTypeId}`)
			:	of(undefined).pipe(catchError(() => of(undefined)));
	}

	getBookApplicationEligibility(applicationId: number): Observable<BookEligibility> {
		return this.http.get<BookEligibility>(`${this.relativeUrl}${applicationId}/eligibility-book-application`);
	}

	getApplicationExpenses(applicationId: number, applicationLevelCurrency: boolean): void {
		this.http
			.get<
				Array<ApplicationExpense>
			>(`${this.relativeUrl}${applicationId}/application-expenses`, { params: this.getApplicationLevelQueryParam(applicationLevelCurrency) })
			.subscribe((applicationExpenses) => {
				this.applicationExpensesSubject.next(applicationExpenses);
			});
	}

	getEducationGoal(participantId: number, educationGoalId: number): void {
		this.http
			.get<EducationGoal>(`participants/${participantId}/education-goals/${educationGoalId}`)
			.pipe(catchError(() => of(undefined)))
			.subscribe((educationGoal) => {
				this.applicationDataSubject.next(educationGoal);
			});
	}

	getParticipantApprovedEdGoals(participantId: number, activeProductId: number): Observable<ApplicationSearchResult> {
		const queryParams: HttpParamObject = HttpUtilityService.toPlainHttpParams({
			programFeatureCode:
				activeProductId === PRODUCT_TYPES.educationPathways ? constants.programFeatures.pathways.code : '',
			recordsPerPage: 1,
			index: 1
		});

		return this.http.get<ApplicationSearchResult>(
			`participants/${participantId}/education-goals/approved`,
			queryParams
		);
	}

	cancelApplicationDocumentAction(participantId: number, educationGoalId: number): Observable<boolean> {
		return this.http.get<boolean>(
			`participants/${participantId}/education-goals/${educationGoalId}/is-education-goal-cancel`
		);
	}

	hasCompletedEdGoals(participantId: number, isPathway: boolean): Observable<boolean> {
		const queryParams: HttpParamObject = HttpUtilityService.toPlainHttpParams({
			isPathway
		});

		return this.http.get<boolean>(`participants/${participantId}/education-goals/has-completed-edgoals`, queryParams);
	}

	getApplicationExpenseTypes(applicationId: number): Observable<Array<ExpenseType>> {
		return this.http
			.get<Array<ExpenseType>>(`${this.relativeUrl}${applicationId}/expenses/expenseTypes`)
			.pipe(catchError(() => of([])));
	}

	refreshApplicationCourseExpenses(applicationId: number, courseId: number): void {
		this.http
			.get<Array<Expense>>(`${this.relativeUrl}${applicationId}/courses/${courseId}/expenses`)
			.subscribe((expenses) => {
				this.applicationCourseExpensesSubject.next({ courseId, expenses });
			});
	}

	refreshApplicationTotalCreditHours(applicationId: number): void {
		this.http
			.get<Array<Course>>(`${this.relativeUrl}${applicationId.toString()}/application-courses`)
			.subscribe((courses: Array<Course>) => {
				this.applicationTotalCreditHoursSubject.next(CreditHoursService.getTotalCreditHours(courses));
			});
	}

	createCourseExpenses(
		applicationId: number,
		courseId: number,
		expenses: Array<Expense>,
		applicationLevelCurrency: boolean
	): Observable<Array<Expense>> {
		return this.http.post<Array<Expense>>(
			`${this.relativeUrl}${applicationId}/courses/${courseId}/expenses`,
			expenses,
			{ params: this.getApplicationLevelQueryParam(applicationLevelCurrency) }
		);
	}

	createApplicationExpenses(
		applicationId: number,
		applicationExpenses: Array<ApplicationExpense>,
		applicationLevelCurrency: boolean
	): Observable<Array<ApplicationExpense>> {
		return this.http.post<Array<ApplicationExpense>>(
			`${this.relativeUrl}${applicationId}/application-expenses`,
			applicationExpenses,
			{ params: this.getApplicationLevelQueryParam(applicationLevelCurrency) }
		);
	}

	isDataShareEnabled(applicationId: number): Observable<boolean> {
		return this.http.get<boolean>(`applications/${applicationId}/is-datashare-enabled`, {
			context: withCache({
				version: constants.LOCAL_STORAGE.API_CACHE.VERSION_V1,
				key: `isDataShare-${applicationId}`
			})
		});
	}

	getChildApplications(applicationId: number): Observable<Array<MicroApplication>> {
		return this.http.get<Array<MicroApplication>>(`${this.relativeUrl}${applicationId}/child-applications`);
	}

	getRelatedApplications(applicationId: number): Observable<Array<FlatApplication>> {
		return this.http
			.get<Array<FlatApplication>>(`${this.relativeUrl}${applicationId}/linked-applications`)
			.pipe(catchError(() => of([])));
	}

	bulkUpdateApplications(applicationPendingReviewQueue: Array<ApplicationPendingReviewQueue>): Observable<void> {
		return this.http.post<void>(`${this.relativeUrl}pending-review-queue`, applicationPendingReviewQueue);
	}

	getParentApplication(applicationId: number): Observable<Application> {
		return this.http.get<Application>(`${this.relativeUrl}${applicationId}`);
	}

	createSavings(applicationId: number, savings: Savings, applicationLevelCurrency: boolean): Observable<Savings> {
		return this.http.post<Savings>(`${this.relativeUrl}${applicationId}/savings`, savings, {
			params: this.getApplicationLevelQueryParam(applicationLevelCurrency)
		});
	}

	createRelatedApplication(relatedApps: CreatePartialRequest<Application>): Observable<Application> {
		return this.http.post<Application>(`${this.relativeUrl}related-application`, relatedApps);
	}

	getAppSavings(applicationId: number, applicationLevelCurrency?: boolean): Observable<Array<Savings>> {
		return this.http.get<Array<Savings>>(`${this.relativeUrl}${applicationId}/savings`, {
			params: this.getApplicationLevelQueryParam(applicationLevelCurrency)
		});
	}

	getSavingsCategories(): Observable<Array<SavingsSubCategory>> {
		return this.http.get<Array<SavingsSubCategory>>('savings/saving-sub-categories');
	}

	appEligibleForClosing(applicationId: number): Observable<void> {
		return this.http.get<void>(`applications/${applicationId}/app-eligible-for-closing`);
	}

	appReadyForPayment(applicationId: number): Observable<{ message: string }> {
		return this.http.get<{ message: string }>(`applications/${applicationId}/app-ready-for-payment`);
	}

	isProgramPaymentVerificationRequired(applicationId: number): Observable<boolean> {
		return this.http.get<boolean>(`${this.relativeUrl}${applicationId}/is-payment-verification-required`);
	}

	getApplicationPaymentVerification(applicationId: number): Observable<ApplicationPaymentVerification> {
		return this.http.get<ApplicationPaymentVerification>(`${this.relativeUrl}${applicationId}/payment-verification`);
	}

	saveApplicationPaymentVerification(
		applicationId: number,
		applicationPaymentVerification: ApplicationPaymentVerification
	): Observable<ApplicationPaymentVerification> {
		return this.http.post<ApplicationPaymentVerification>(
			`${this.relativeUrl}${applicationId}/payment-verification`,
			applicationPaymentVerification
		);
	}

	hasUnreviewedDocumentsOrComments(applicationId: number): Observable<boolean> {
		return this.http.get<boolean>(`${this.relativeUrl}${applicationId}/has-unreviewed-documents-comments`);
	}

	getApplicationsHistoryAdmin(participantId: number, benefitPeriod: string): Observable<HttpResponse<Blob>> {
		const queryParams = new HttpParams().set('participantId', participantId).set('benefitPeriod', benefitPeriod);

		return this.http.post<any>(`${this.relativeUrl}csv-export/admin`, undefined, {
			responseType: 'blob' as 'json',
			observe: 'response',
			params: queryParams
		});
	}

	isAllowedForAddingCourse(applicationId: number): Observable<void> {
		return this.http.get<void>(`${this.relativeUrl}${applicationId}/is-add-course-allowed`);
	}

	submitApplication(applicationId: number): Observable<void> {
		return this.http.post<void>(`${this.relativeUrl}${applicationId}/submission`, {});
	}

	getApplicationLevelQueryParam(applicationLevelCurrency: boolean): HttpParams {
		return new HttpParams().append('applicationLevelCurrency', applicationLevelCurrency);
	}

	getApplicationTypeByApplicationId(applicationId: number): Observable<ApplicationType> {
		return this.http.get<ApplicationType>(`${this.relativeUrl}${applicationId}/application-type`);
	}

	getApplicationStatus(applicationId: number): Observable<ApplicationStatus> {
		return this.http.get<ApplicationStatus>(`${this.relativeUrl}${applicationId?.toString()}/app-status`);
	}

	getCombinedExpensesByApplicationId(applicationId: number): Observable<Array<CombinedExpense>> {
		return this.http.get<Array<CombinedExpense>>(`${this.relativeUrl}${applicationId}/combined-expenses`);
	}

	createPaymentApplicationByAdmin(
		currentUserId: number,
		applicationId: number,
		requestedPaymentAmount: number,
		requestedPaymentDate: number
	): Observable<ApplicationSubmission> {
		return this.http.post<ApplicationSubmission>(
			`${this.relativeUrl}${applicationId}/loan-payment-request-by-admin`,
			undefined,
			{
				params: {
					currentUserId: currentUserId,
					requestedPaymentAmount: requestedPaymentAmount,
					requestedPaymentDate: requestedPaymentDate
				}
			}
		);
	}

	stopPayment(applicationId: number): Observable<void> {
		return this.http.post<void>(`${this.relativeUrl}${applicationId}/stop-payment`, {});
	}

	updateApplication(applicationId: number, application: Application): Observable<Application> {
		return this.http.put<Application>(`${this.relativeUrl}${applicationId}`, application);
	}
}
